import React, { useEffect, useState } from 'react';
import { Form, Button, Card, Row, Col, ButtonGroup } from 'react-bootstrap';
import api from '../../services/api';
import Layout from '../../components/Layout';
import { Container } from 'react-bootstrap';
import TopBar from '../../components/TopBar';
import LeaveHistory from './LeaveHistory';
import FeedbackModal from '../../components/FeedbackModal';


const LeaveApply = () => {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    leave_for: 'Full Day'
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalProps, setModalProps] = useState({
    title: '',
    message: '',
    variant: 'success',
    type: 'success'
  });

  // Add error display component
  const ErrorDisplay = ({ error }) => {
    if (!error) return null;
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  };
  const [viewMode, setViewMode] = useState('apply');
  const [quotas, setQuotas] = useState({ quotas: [], holidays: {}, holiday_counts: { national: 0, festival: 0 } });
  const [unpaidLeavesCount, setUnpaidLeavesCount] = useState(0);

  useEffect(() => {
    // First fetch all leave types
    api.get('/leave/types')
      .then(response => {
        setTypes(response.data);
      })
      .catch(error => {
        console.error('Error fetching leave types:', error);
        // Fallback to fetching from quotas if direct endpoint fails
        api.get('/leave/quotas')
          .then(res => {
            setTypes(
              res.data.map(q => ({
                id: q.leave_type.id,
                name: q.leave_type.name
              }))
            );
          })
          .catch(err => console.error('Error fetching leave types from quotas:', err));
      });

    // Fetch unpaid leaves for current year
    api.get('/unpaid-leaves/current-year')
      .then(response => {
        setUnpaidLeavesCount(response.data.unpaid_leaves || 0);
      })
      .catch(error => {
        console.error('Error fetching unpaid leaves:', error);
      });

  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    const today = new Date().toISOString().split('T')[0];
    const startDate = form.start_date;
    const endDate = form.end_date;
    
    if (!form.leave_type_id) {
      setFormError('Please select a leave type');
      return;
    }
    
    if (!form.leave_for) {
      setFormError('Please select leave duration');
      return;
    }
    
    if (!startDate || !endDate) {
      setFormError('Please select both start and end dates');
      return;
    }
    
    if (startDate > endDate) {
      setFormError('Start date must be before end date');
      return;
    }
    
    // Date validation for past dates removed to allow retroactive leave applications
    
    try {
      setIsSubmitting(true);
      const response = await api.post('/leaves', {
        ...form,
        leave_for: form.leave_for
      });
      
      if (response.status === 200) {
        setModalProps({
          title: 'Success',
          message: 'Leave application submitted successfully!',
          variant: 'success',
          type: 'success'
        });
        setShowModal(true);
        setForm({ leave_type_id: '', start_date: '', end_date: '', reason: '', leave_for: 'Full Day' });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to apply leave. Please try again.';
      setModalProps({
        title: 'Error',
        message: errorMessage,
        variant: 'danger',
        type: 'error'
      });
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const fetchQuotas = async () => {
    try {
      const response = await api.get('/leave/quotas-and-holidays');
      setQuotas(response.data);
    } catch (error) {
      console.error('Error fetching leave quotas:', error);
    }
  };

  useEffect(() => {
    fetchQuotas();
  }, []);

  // Process leave data and get leave type data
  const unpaidLeaves = quotas.quotas.find(q => q.leave_type_name === 'Loss of Pay')?.used || unpaidLeavesCount;
  const floatingHoliday = quotas.quotas.find(q => 
    q.leave_type_name.includes('Floating Holiday') || 
    q.leave_type_name.includes('Floating')
  ) || { used: 0, allocated: 0 };

  // Work From Home (WFH) leave type support (by name variants)
  const workFromHome = quotas.quotas.find(q => {
    const name = (q.leave_type_name || '').toLowerCase();
    return name.includes('work from home') || name === 'wfh' || name.includes('remote');
  }) || { used: 0, allocated: 0 };

  const leaveData = quotas.quotas.reduce((acc, quota) => {
    if (quota.leave_type_name === 'Annual Leave' || quota.leave_type_name === 'Annual Leaves') {
      acc.annualLeave = quota;
    } else if (quota.leave_type_name === 'Sick Leave' || quota.leave_type_name === 'Emergency Leave') {
      acc.sickLeave = quota;
    } else if (quota.leave_type_name === 'Compensatory Off') {
      acc.compOff = quota;
    }
    return acc;
  }, { 
    annualLeave: { used: 0, allocated: 0 },
    sickLeave: { used: 0, allocated: 0 },
    compOff: { used: 0, allocated: 0 }
  });

  const normalizeRange = (input) => {
    if (input === undefined || input === null) return "0";
    return parseFloat(input).toString();
  };

  // Leave summary cards data
  const leaveSummary = [
    { 
      label: "Annual Leave", 
      value: `${normalizeRange(leaveData.annualLeave.used)}/${normalizeRange(leaveData.annualLeave.allocated)}`, 
      bg: "#ffe5e5" 
    },
    { 
      label: "Emergency Leave", 
      value: `${normalizeRange(leaveData.sickLeave.used)}/${normalizeRange(leaveData.sickLeave.allocated)}`, 
      bg: "#f5f5dc" 
    },
    { 
      label: "Compensatory Off", 
      value: `${normalizeRange(leaveData.compOff.used)}/${normalizeRange(leaveData.compOff.allocated)}`, 
      bg: "#e6f7ff" 
    },
    { 
      label: "Floating Holiday", 
      value: `${normalizeRange(floatingHoliday.used)}/${normalizeRange(floatingHoliday.allocated)}`, 
      bg: "#4dbf89" 
    },
    { 
      label: "Work From Home", 
      value: `${normalizeRange(workFromHome.used)}/${normalizeRange(workFromHome.allocated)}`,
      bg: "#e8f0fe" 
    },
    { 
      label: "Loss Of Pay", 
      value: `${normalizeRange(unpaidLeaves)}/${normalizeRange(quotas.quotas.find(q => q.leave_type_name === 'Loss of Pay')?.allocated || 0)}`,
      bg: "#eef" 
    }
  ];

  return (
    <Layout>
      <TopBar />
      <Container fluid className="mt-4">
        <Row className="m-0 p-0">
          {leaveSummary.map((item, idx) => (
            <Col key={idx} xs={12} sm={6} md={4} lg={2} className="mb-3">
              <Card className="shadow-sm" style={{ background: '#fff', cursor: 'pointer', minHeight: '90px' }}>
                <Card.Body className="d-flex align-items-center">
                  <div style={{ width: '16px', height: '16px', backgroundColor: item.bg, marginRight: '10px' }}></div>
                  <div>
                    <div className="fw-bold">{item.value}</div>
                    <div className="text-muted small summary-card">{item.label.toUpperCase()}</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row>
          <Col sm={12} md={12} lg={12}>
              
            <Card className="shadow-sm">
                <div className="text-end m-4">
                    <ButtonGroup>
                      <Button type="submit" variant="primary" onClick={() => setViewMode('apply')}>
                        Apply Leave
                      </Button>
                      <Button variant="secondary" onClick={() => setViewMode('history')}>
                        Leave Status
                      </Button>
                    </ButtonGroup>
                  </div>
          
              <Card.Body>
                {showModal && (
                  <FeedbackModal 
                    show={showModal} 
                    onHide={() => setShowModal(false)} 
                    title={modalProps.title} 
                    message={modalProps.message} 
                    variant={modalProps.variant} 
                    type={modalProps.type} 
                  />
                )}
                
                {viewMode === 'apply' && (
                  <Col sm={6} md={6} lg={6}>
                    <Form onSubmit={handleSubmit}>
                      {formError && <ErrorDisplay error={formError} />}
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Leave Type</Form.Label>
                            <Form.Select
                              value={form.leave_type_id}
                              onChange={(e) =>
                                setForm({ ...form, leave_type_id: e.target.value })
                              }
                              required
                            >
                              <option value="">-- Select Leave Type --</option>
                              {types.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Leave Duration</Form.Label>
                            <Form.Select
                              value={form.leave_for}
                              onChange={(e) =>
                                setForm({ ...form, leave_for: e.target.value })
                              }
                              required
                            >
                              <option value="">Please Select</option>
                              <option value="Full Day">Full Day</option>
                              <option value="Half Day - Morning">Half Day - Morning</option>
                              <option value="Half Day - Afternoon">Half Day - Afternoon</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                              type="date"
                              value={form.start_date}
                              onChange={(e) =>
                                setForm({ ...form, start_date: e.target.value })
                              }
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                              type="date"
                              value={form.end_date}
                              onChange={(e) =>
                                setForm({ ...form, end_date: e.target.value })
                              }
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                  

                   

                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Reason</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              placeholder="Optional reason..."
                              value={form.reason}
                              onChange={(e) =>
                                setForm({ ...form, reason: e.target.value })
                              }
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="text-end">
                        <Button type="submit" variant="primary" disabled={isSubmitting}>
                          {isSubmitting ? 'Submitting...' : 'Apply Leave'}
                        </Button>
                      </div>
                    </Form>
                  </Col>
                  ) 
                }
                  
                 {viewMode === 'history' && (
                  <Col sm={12} md={12} lg={12}>
                    <LeaveHistory />
                  </Col>
                  )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default LeaveApply;
